import React from 'react';
import styled from 'styled-components';
import { stringToNumberOrZero } from 'utils/formatters/stringToNumberOrZero';
import { ReportData } from 'utils/types/AnalyticPanelReport';

import { PDF_EXPORT_CLASS } from './pdfExportClass';

const ReportSection = styled.div`
    background: var(--color-white);
    border-radius: var(--box-border-radius);
    padding: 24px;
    margin-bottom: 20px;

    .${PDF_EXPORT_CLASS} & {
        border: 1px solid var(--pdf-card-border);
    }
`;

const SectionTitle = styled.h2`
    font-size: 16px;
    font-weight: 600;
    color: var(--color-primary);
    margin-bottom: 20px;
`;

const ProgressBarContainer = styled.div`
    margin-bottom: 16px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const ProgressBarLabel = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--color-primary);
`;

const ProgressBar = styled.div`
    height: 32px;
    background: var(--color-alt-second);
    border-radius: 16px;
    overflow: hidden;
    display: flex;
`;

const ProgressBarFill = styled.div<{ $width: number; $color: string }>`
    width: ${({ $width }) => Math.min($width, 100)}%;
    background: ${({ $color }) => $color};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 13px;
    font-weight: 600;
    min-width: ${({ $width }) => $width > 5 ? '50px' : '0'};
    transition: width 0.3s ease;
`;

interface MarginSectionProps {
    data: ReportData;
}

export const MarginSection: React.FC<MarginSectionProps> = ({ data }) => {
    const { calculation } = data;

    if (!calculation) return null;

    const margin = stringToNumberOrZero(calculation.margin);
    const roi = stringToNumberOrZero(calculation.roi);
    const flipperRoe = stringToNumberOrZero(calculation.flipperRoe);
    const investorRoe = stringToNumberOrZero(calculation.investorRoe);

    return (
        <ReportSection>
            <SectionTitle>Wskaźniki rentowności</SectionTitle>

            <ProgressBarContainer>
                <ProgressBarLabel>
                    <span>Marża - stosunek zysku do ceny sprzedaży</span>
                    <span>{margin.toFixed(1)}%</span>
                </ProgressBarLabel>
                <ProgressBar>
                    <ProgressBarFill $width={margin} $color="var(--color-alt)">
                        {margin > 5 ? `${margin.toFixed(1)}%` : ''}
                    </ProgressBarFill>
                </ProgressBar>
            </ProgressBarContainer>

            <ProgressBarContainer>
                <ProgressBarLabel>
                    <span>Stopa zwrotu - ROI</span>
                    <span>{roi.toFixed(1)}%</span>
                </ProgressBarLabel>
                <ProgressBar>
                    <ProgressBarFill $width={roi} $color="var(--color-alt)">
                        {roi > 5 ? `${roi.toFixed(1)}%` : ''}
                    </ProgressBarFill>
                </ProgressBar>
            </ProgressBarContainer>

            <ProgressBarContainer>
                <ProgressBarLabel>
                    <span>Stopa zwrotu - ROE Flippera</span>
                    <span>{flipperRoe.toFixed(1)}%</span>
                </ProgressBarLabel>
                <ProgressBar>
                    <ProgressBarFill $width={flipperRoe} $color="var(--color-alt)">
                        {flipperRoe > 5 ? `${flipperRoe.toFixed(1)}%` : ''}
                    </ProgressBarFill>
                </ProgressBar>
            </ProgressBarContainer>

            <ProgressBarContainer>
                <ProgressBarLabel>
                    <span>Stopa zwrotu - ROE Inwestora</span>
                    <span>{investorRoe.toFixed(1)}%</span>
                </ProgressBarLabel>
                <ProgressBar>
                    <ProgressBarFill $width={investorRoe} $color="var(--color-alt)">
                        {investorRoe > 5 ? `${investorRoe.toFixed(1)}%` : ''}
                    </ProgressBarFill>
                </ProgressBar>
            </ProgressBarContainer>
        </ReportSection>
    );
};
